<template>
  <div class="bubbles-container" :class="{ 'is-dark': themeStore.isDark }">
    <div 
      v-for="bubble in bubbles" 
      :key="bubble.id" 
      class="bubble"
      :style="{
        left: bubble.x + 'px',
        top: bubble.y + 'px',
        width: bubble.size + 'px',
        height: bubble.size + 'px',
        '--wobble-x': bubble.wobbleX + 'px',
        '--duration': bubble.duration + 's'
      }"
    ></div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useThemeStore } from '../../stores/theme'

const themeStore = useThemeStore()

const bubbles = ref([])
let bubbleId = 0
let isPressing = false
let isRubberbandDragging = false
let currentX = 0
let currentY = 0
let lastSpawnX = 0
let lastSpawnY = 0
let pressInterval = null

const updateCoords = (e) => {
  if (e.touches && e.touches.length > 0) {
    currentX = e.touches[0].clientX
    currentY = e.touches[0].clientY
  } else if (e.clientX !== undefined) {
    currentX = e.clientX
    currentY = e.clientY
  }
}

const spawnBubbles = (count = 1) => {
  if (isRubberbandDragging) return

  for (let i = 0; i < count; i++) {
    const id = bubbleId++
    // Micro-bolhas discretas e delicadas (estilo champagne fino): 2px a 5px
    const size = Math.random() < 0.75 ? (Math.random() * 2 + 2) : (Math.random() * 2.5 + 3.5)
    
    // Espalha suavemente ao redor do ponto de clique
    const offsetX = (Math.random() - 0.5) * 24
    const offsetY = (Math.random() - 0.5) * 24
    
    // Oscilação lateral elegante
    const wobbleX = (Math.random() * 30 - 15)
    const duration = Math.random() * 1.0 + 0.8 // 0.8s a 1.8s subindo

    bubbles.value.push({
      id,
      x: currentX + offsetX - (size / 2),
      y: currentY + offsetY - (size / 2),
      size,
      wobbleX,
      duration
    })

    lastSpawnX = currentX
    lastSpawnY = currentY

    // Limpa a bolha logo após o fim da animação
    setTimeout(() => {
      bubbles.value = bubbles.value.filter(b => b.id !== id)
    }, duration * 1000)
  }
}

const onDown = (e) => {
  isPressing = true
  updateCoords(e)
  spawnBubbles(1) // Disparo de apenas 1 micro-bolha ao clicar
  
  if (pressInterval) clearInterval(pressInterval)
  
  // Intervalo calmo ao manter pressionado (350ms) gerando no máximo 1 micro-bolha
  pressInterval = setInterval(() => {
    if (isPressing) spawnBubbles(1)
  }, 350)
}

const onMove = (e) => {
  if (isPressing) {
    updateCoords(e)
    const dist = Math.hypot(currentX - lastSpawnX, currentY - lastSpawnY)
    // Só gera se tiver se movido razoavelmente (mais de 65px) e com chance controlada (15%)
    if (dist > 65 && Math.random() > 0.85) {
      spawnBubbles(1)
    }
  }
}

const onUp = () => {
  isPressing = false
  if (pressInterval) {
    clearInterval(pressInterval)
    pressInterval = null
  }
}

const onRubberbandDrag = (e) => {
  isRubberbandDragging = e.detail
}

const onBubbleBurst = (e) => {
  const x = e.detail?.x ?? (window.innerWidth - 220)
  const y = e.detail?.y ?? (window.innerHeight - 90)
  const count = e.detail?.count ?? 16

  for (let i = 0; i < count; i++) {
    const id = bubbleId++
    // Tamanhos de micro-champagne real: 2px a 6px
    const size = Math.random() < 0.75 ? (Math.random() * 2.5 + 2) : (Math.random() * 3 + 4)
    // Dispersão horizontal ampla e natural
    const offsetX = (Math.random() - 0.5) * 320
    const offsetY = (Math.random() - 0.5) * 60
    // Oscilação lateral elegante
    const wobbleX = (Math.random() * 100 - 50)
    const duration = Math.random() * 1.5 + 1.2

    setTimeout(() => {
      bubbles.value.push({
        id,
        x: x + offsetX - (size / 2),
        y: y + offsetY - (size / 2),
        size,
        wobbleX,
        duration
      })
      setTimeout(() => {
        bubbles.value = bubbles.value.filter(b => b.id !== id)
      }, duration * 1000)
    }, i * 40)
  }
}

onMounted(() => {
  window.addEventListener('burst-bubbles', onBubbleBurst)
  window.addEventListener('rubberband-drag', onRubberbandDrag)
  window.addEventListener('mousedown', onDown, { capture: true })
  window.addEventListener('mousemove', onMove, { capture: true, passive: true })
  window.addEventListener('mouseup', onUp, { capture: true })
  window.addEventListener('mouseleave', onUp)
  window.addEventListener('blur', onUp)
  
  window.addEventListener('touchstart', onDown, { capture: true, passive: true })
  window.addEventListener('touchmove', onMove, { capture: true, passive: true })
  window.addEventListener('touchend', onUp, { capture: true })
  window.addEventListener('touchcancel', onUp, { capture: true })
})

onUnmounted(() => {
  window.removeEventListener('burst-bubbles', onBubbleBurst)
  window.removeEventListener('rubberband-drag', onRubberbandDrag)
  window.removeEventListener('mousedown', onDown, { capture: true })
  window.removeEventListener('mousemove', onMove, { capture: true })
  window.removeEventListener('mouseup', onUp, { capture: true })
  window.removeEventListener('mouseleave', onUp)
  window.removeEventListener('blur', onUp)
  window.removeEventListener('touchstart', onDown, { capture: true })
  window.removeEventListener('touchmove', onMove, { capture: true })
  window.removeEventListener('touchend', onUp, { capture: true })
  window.removeEventListener('touchcancel', onUp, { capture: true })
})
</script>

<style scoped>
.bubbles-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  z-index: 99999;
  overflow: hidden;
}

/* Partícula oca para o Modo Claro */
.bubble {
  position: absolute;
  background-color: transparent;
  border: 1px solid rgba(0, 0, 0, 0.35);
  border-radius: 50%;
  pointer-events: none;
  animation: floatUp var(--duration) linear forwards;
  opacity: 0;
}

/* Modo Escuro - bolhas suaves e discretas para não sobrecarregar a visão */
.bubbles-container.is-dark .bubble {
  border-color: rgba(255, 255, 255, 0.32);
  box-shadow: 0 0 2px rgba(255, 255, 255, 0.12);
}

@keyframes floatUp {
  0% {
    transform: translateY(0) translateX(0) scale(1);
    opacity: 0.6;
  }
  50% {
    transform: translateY(-50px) translateX(var(--wobble-x)) scale(1.1);
    opacity: 0.35;
  }
  100% {
    transform: translateY(-110px) translateX(calc(var(--wobble-x) * -1)) scale(1.25);
    opacity: 0;
  }
}
</style>
